// src/components/customers/CreateCustomerDrawer.tsx
import React, { useEffect, useState } from 'react';
import { Collapse, Drawer, Form, Input, Button, Space, message, Select } from 'antd';
import { createCustomer, type CreateCustomerRequest, listServices } from '../../services/customers';
import { listSectors, type SectorDTO } from '../../services/sector';
import { convertContactToCustomer, type ContactDTO } from '../../services/contacts';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;        // richiama per ricaricare la tabella
  /** Se valorizzato, il form si precompila coi dati del contatto */
  contact?: ContactDTO | null;
};

// Estendo i valori del form rispetto al tipo FE attuale
type FormValues = CreateCustomerRequest & {
  sectorId?: number | null;
  userPassword?: string;   // (rimane nel tipo, non introdurre nuove UI)
  serviceIds?: number[];
  createUserAccess?: boolean; // (rimane nel tipo, non introdurre nuove UI)
};

const CreateCustomerDrawer: React.FC<Props> = ({ open, onClose, onCreated, contact }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);

  const [serviceOptions, setServiceOptions] = useState<Array<{ label: string; value: number }>>([]);
  const [sectorOptions, setSectorOptions] = useState<Array<{ label: string; value: number; icon?: string | null }>>([]);

  // Helpers safe
  const safe = (s?: string | null) => (typeof s === 'string' ? s.trim() : '');
  const fromAny = (obj: any, key: string): string | undefined =>
    (typeof obj?.[key] === 'string' && obj[key].trim() ? obj[key].trim() : undefined);

  const businessNameFromContact = (c?: ContactDTO | null) => {
    const bn = fromAny(c, 'businessName');
    if (bn) return bn;
    const first = fromAny(c, 'firstName');
    const last  = fromAny(c, 'lastName');
    const full = [first, last].filter(Boolean).join(' ').trim();
    if (full) return full;
    return fromAny(c, 'field1') || fromAny(c, 'field2') || '';
  };

  const emailFromContact = (c?: ContactDTO | null) =>
    fromAny(c, 'email') || fromAny(c, 'field3') || undefined;

  const phoneFromContact = (c?: ContactDTO | null) =>
    fromAny(c, 'phone') || fromAny(c, 'field4') || undefined;

  // Carica servizi e settori
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [services, sectors] = await Promise.all([listServices(), listSectors(true)]);
        setServiceOptions((services || []).map(s => ({ label: s.name, value: s.id })));
        setSectorOptions((sectors || []).map((s: SectorDTO) => ({
          label: s.name,
          value: s.id,
          icon: s.icon ?? undefined,
        })));
      } catch {
        setServiceOptions([]);
        setSectorOptions([]);
      }
    })();
  }, [open]);

  // Prefill dai dati del contatto (se presente) — completamente null-safe
  useEffect(() => {
    if (!open) return;
    const preset: Partial<FormValues> = {
      businessName: businessNameFromContact(contact) || undefined,
      email: emailFromContact(contact),
      phone: phoneFromContact(contact),
      notes: (contact?.notes && contact.notes.trim()) || undefined,
      credentials: undefined,
      sectorId: contact?.sector?.id ?? undefined,
      country: 'IT',
      createUserAccess: false,
      userEmail: emailFromContact(contact),
    };
    form.setFieldsValue(preset as any);
  }, [open, contact, form]);

  // Reset alla chiusura
  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const payload: Partial<CreateCustomerRequest> = {
        businessName: values.businessName?.trim() || '',
        vatNumber: values.vatNumber?.trim() || undefined,
        taxCode: values.taxCode?.trim() || undefined,
        email: values.email?.trim() || undefined,
        phone: values.phone?.trim() || undefined,
        address: values.address?.trim() || undefined,
        city: values.city?.trim() || undefined,
        postalCode: values.postalCode?.trim() || undefined,
        province: values.province?.trim() || undefined,
        country: values.country || 'IT',
        notes: values.notes || undefined,
        credentials: values.credentials?.trim() || undefined,
        sectorId: values.sectorId ?? undefined,
        serviceIds: values.serviceIds && values.serviceIds.length ? values.serviceIds : undefined,
      };

      // 1) Crea cliente
      await createCustomer(payload as CreateCustomerRequest);

      // 2) SE è una conversione, marca il contatto come cliente (isCustomer=true + data)
      if (contact?.id) {
        try {
          await convertContactToCustomer(contact.id);
        } catch (err) {
          // non bloccare il flusso se la marcatura fallisce
          console.warn('convertContactToCustomer failed:', err);
        }
      }

      message.success(contact ? 'Contatto trasformato in cliente' : 'Cliente creato con successo');
      form.resetFields();
      onCreated();
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return; // validazione AntD
      message.error(err?.message || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  const isConvertMode = !!contact;

  return (
    <Drawer
      title={isConvertMode ? 'Trasforma in Cliente' : 'Nuovo Cliente'}
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Salva
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="businessName"
          label="Ragione sociale"
          rules={[{ required: true, message: 'La ragione sociale è obbligatoria' }]}
        >
          <Input placeholder="Es. Tech Solutions Srl" />
        </Form.Item>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="vatNumber" label="P.IVA" style={{ flex: 1 }}>
            <Input placeholder="IT01234567890" />
          </Form.Item>
          
          <Form.Item name="sectorId" label="Settore" style={{ flex: 1 }}>
            <Select
              allowClear
              showSearch
              placeholder="Seleziona settore"
              options={sectorOptions}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Space.Compact>

        <Collapse
          destroyInactivePanel={false}
          expandIconPosition="end"
          style={{ marginTop: 8, marginBottom: 16 }}
          items={[
            {
              key: 'contact-address',
              label: 'Contatti e indirizzo',
              children: (
                <>
                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item
                      name="email"
                      label="Email"
                      style={{ flex: 1 }}
                      rules={[{ type: 'email', message: 'Email non valida' }]}
                    >
                      <Input placeholder="contatti@azienda.it" />
                    </Form.Item>
                    <Form.Item name="phone" label="Telefono" style={{ flex: 1 }}>
                      <Input placeholder="+39 02 1234567" />
                    </Form.Item>
                  </Space.Compact>

                  <Form.Item name="address" label="Indirizzo">
                    <Input placeholder="Via Roma 1" />
                  </Form.Item>

                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item name="city" label="Città" style={{ flex: 1 }}>
                      <Input placeholder="Milano" />
                    </Form.Item>
                    <Form.Item name="postalCode" label="CAP" style={{ flex: 1 }}>
                      <Input placeholder="20100" />
                    </Form.Item>
                  </Space.Compact>

                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item name="province" label="Provincia" style={{ flex: 1 }}>
                      <Input placeholder="MI" />
                    </Form.Item>
                    <Form.Item name="country" label="Paese" initialValue="IT" style={{ flex: 1 }}>
                      <Input placeholder="IT" />
                    </Form.Item>
                  </Space.Compact>
                </>
              ),
            },
          ]}
        />

        <Form.Item name="notes" label="Note">
          <Input.TextArea placeholder="Note interne..." autoSize={{ minRows: 3 }} />
        </Form.Item>

        <Form.Item name="credentials" label="Credenziali">
          <Input.TextArea placeholder="Accessi, password o note riservate..." autoSize={{ minRows: 3 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CreateCustomerDrawer;
